import axios from "axios";
import { LIVENESS_API, VERIFICATION_API } from "../../../config/microserviceApis";
import { createLogSession } from "./logs/logs";

export const verificationAxios = axios.create();

verificationAxios.interceptors.request.use(
    (config) => {
        if (!config.baseURL) {
            let url = VERIFICATION_API;
            if (url) {
                config.baseURL = url;
            }
        }

        const token = localStorage.getItem("a_t");
        if (!token) {
            window.location.href = "/#/login";
            return Promise.reject(new Error("No auth token"));
        }
        config.headers.Authorization = `Bearer ${token}`;

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

export async function apiGetUserVerificationFlows(data) {
    const payload = {
        filter: {},
        sort: {},
        limit: 0,
        offset: 0,
        ...data,
    };

    try {
        const response = await verificationAxios.post(`/api/flow/setting/get`, payload);
        return response?.data;
    } catch (e) {
        console.error(e);
        throw e;
    }
}

export async function apiAssignUserVerificationProfile(flowSettingId) {
    try {
        const response = await verificationAxios.post(`/api/user_verification/profile/assign`, flowSettingId, {
            headers: {
                "Content-Type": "text/plain"
            }
        });
        return response?.data;
    } catch (e) {
        console.error(e);
        throw e;
    }
}

export async function apiGetUserVerificationProfile(data, urlVerification= "") {
    const payload = {
        filter: {},
        sort: {},
        limit: 0,
        offset: 0,
        ...data,
    };

    const config = {};
    if (urlVerification) {
        config.baseURL = urlVerification;
    }

    try {
        const response = await verificationAxios.post(`/api/user_verification/profile/get`, payload, config);
        return response?.data;
    } catch (e) {
        console.error(e);
        throw e;
    }
}

export async function apiGetUserVerificationHistories(data) {
    const payload = {
        filter: {},
        sort: {},
        limit: 0,
        offset: 0,
        ...data,
    };

    try {
        const response = await verificationAxios.post(`/api/user_verification/history/get`, payload);
        return response?.data;
    } catch (e) {
        console.error(e);
        throw e;
    }
}

export async function apiInitializeUserVerificationHistory(tierSettingId) {
    try {
        const response = await verificationAxios.post(`/api/user_verification/history/init`, tierSettingId, {
            headers: {
                "Content-Type": "application/json"
            }
        });
        return response?.data;
    } catch (e) {
        console.error(e);
        throw e;
    }
}

export async function apiDuplicateUserVerificationHistory(payload) {
    const log = createLogSession("User", "Verification", "duplicateHistory");
    try {
        const response = await verificationAxios.post(`/api/user_verification/history/duplicate`, payload, {
            headers: {
                "Content-Type": "application/json"
            }
        });
        log.create({ status: "info", description: "Verification history duplicated (continue with last record)", logData: { ...payload } });
        return response?.data;
    } catch (e) {
        const errData = e?.response?.data || { status: "failed", errObj: "System Error" };
        log.create({ status: "error", description: "Duplicate verification history exception", logData: errData });
        console.error(e);
        throw e;
    }
}

export async function apiAbandonUserVerificationProfile() {
    try {
        const response = await verificationAxios.post(`/api/user_verification/profile/abandon`, {}, {
            headers: {
                "Content-Type": "application/json"
            }
        });
        return response?.data;
    } catch (e) {
        console.error(e);
        throw e;
    }
}

export async function apiSaveUserVerificationHistory(data) {
    try {
        const response = await verificationAxios.post(`/api/user_verification/history/save`, data, {
            headers: {
                "Content-Type": "application/json"
            }
        });
        return response?.data;
    } catch (e) {
        console.error(e);
        throw e;
    }
}

export async function apiSubmitUserVerificationHistory(data) {
    try {
        const response = await verificationAxios.post(`/api/user_verification/history/submit`, data, {
            headers: {
                "Content-Type": "application/json"
            }
        });
        return response?.data;
    } catch (e) {
        console.error(e);
        throw e;
    }
}

export async function apiUploadUserVerificationHistoryMedia(formData) {
    try {
        const response = await verificationAxios.post(`/api/user_verification/history/upload`, formData);
        return response?.data;
    } catch (e) {
        console.error(e);
        throw e;
    }
}

export async function apiUpdateUserVerificationHistoryStatus(data) {
    try {
        const response = await verificationAxios.post(`/api/user_verification/history/update_status`, data);
        return response?.data;
    } catch (e) {
        console.error(e);
        throw e;
    }
}

export async function apiGetUserVerificationTiers(data) {
    const payload = {
        filter: {},
        sort: {
            tierLevel: 1
        },
        limit: 0,
        offset: 0,
        ...data,
    };

    try {
        const response = await verificationAxios.post(`/api/tier/setting/get`, payload);
        return response?.data;
    } catch (e) {
        console.error(e);
        throw e;
    }
}

export async function apiDeleteMediaResponse(fileName) {
    const log = createLogSession("User", "Verification", "deleteMedia");
    try {
        const response = await verificationAxios.delete(`/assets/response/${fileName}`);
        log.create({ status: "info", description: "Uploaded file deleted", logData: { fileName } });
        return response?.data;
    } catch (e) {
        const errData = e?.response?.data || { status: "failed", errObj: "System Error" };
        log.create({ status: "error", description: "Delete media exception", logData: errData });
        console.error(e);
        throw e;
    }
}

export async function apiGetMediaResponse(fileRelativePath) {
    try {
        const path = fileRelativePath.startsWith('/') ? fileRelativePath : `/${fileRelativePath}`;
        const response = await verificationAxios.get(path);
        return response?.data;
    } catch (e) {
        console.error(e);
        throw e;
    }
}

export const apiGetVerificationTierSettingsAccessRights = async () => {
    try {
        const response = await verificationAxios.get(`/api/tier/setting/access_right/list`);
        return response?.data;
    } catch (e) {
        throw e;
    }
}

/**
 * Create a one-shot liveness capture link for one liveness field of one history.
 *
 * `idempotencyKey` is frontend-generated with NO server fallback — a missing or
 * empty value fails with `idempotencyKey is required`. Every new attempt needs a
 * FRESH key; reusing a consumed one is rejected by the microservice.
 *
 * The returned `verificationUrl` carries its token in the URL fragment. Open it
 * unmodified — never log, parse, or re-encode it.
 */
export async function apiCreateLivenessLink({historyId, documentSettingId, fieldSettingId, idempotencyKey}) {
    try {
        // NOTE: the field really is named `_id`, not `historyId`, even though the
        // error message for a bad value reads `invalid historyId`.
        const response = await verificationAxios.post(`/user_verification/history/liveness/link/create`, {
            _id: historyId,
            documentSettingId,
            fieldSettingId,
            idempotencyKey,
        }, {
            baseURL: LIVENESS_API,
            headers: {"Content-Type": "application/json"}
        });
        return response?.data;
    } catch (e) {
        // Failures arrive as HTTP 400 with a `status: "failed"` envelope, so axios
        // throws. Hand the envelope back rather than the axios error.
        if (e?.response?.data) return e.response.data;
        console.error(e);
        throw e;
    }
}

/**
 * Poll for the verdict on one liveness field.
 *
 * `{found: false}` is the NORMAL in-progress state, not an error — the result is
 * written by a webhook when the capture session reaches a terminal state.
 * `accepted` and `terminal` are computed server-side; use them, don't re-derive.
 */
export async function apiGetLivenessResult({historyId, fieldSettingId}) {
    try {
        const response = await verificationAxios.post(`/user_verification/history/liveness/result/get`, {
            _id: historyId,
            fieldSettingId,
        }, {
            baseURL: LIVENESS_API,
            headers: {"Content-Type": "application/json"}
        });
        return response?.data;
    } catch (e) {
        if (e?.response?.data) return e.response.data;
        console.error(e);
        throw e;
    }
}

export async function apiCancelVerificationHistory(id) {
    const log = createLogSession("User", "Verification", "cancelHistory");
    try {
        const response = await verificationAxios.post(`/api/user_verification/history/cancel`, { $oid: id }, {
             headers: {
                "Content-Type": "application/json"
            }
        });
        log.create({ status: "info", description: "Verification history cancelled", logData: { historyId: id } });
        return response?.data;
    } catch (e) {
        const errData = e?.response?.data || { status: "failed", errObj: "System Error" };
        log.create({ status: "error", description: "Cancel verification history exception", logData: errData });
        console.error(e);
        throw e;
    }
}
